import type { ChatMemberResponse } from '../services/chatApiService'
import { formatShortName, getFullName } from '@/utils/formatters'

export interface ChatMentionCandidate {
  key: string
  mxid: string
  displayName: string
  /** Текст после `@` в сообщении, напр. «Иванов И.» */
  mentionText: string
  avatar?: string | null
  searchText: string
}

export interface MentionTextSegment {
  type: 'text' | 'mention'
  value: string
  /** MXID, если удалось сопоставить с `m.mentions.user_ids` / участниками комнаты */
  mxid?: string
}

function fallbackNameFromMxid(mxid: string): string {
  return mxid.split(':')[0]?.replace(/^@/, '') || mxid
}

function normalizeMentionLabel(value: string): string {
  return value.replace(/^@/, '').trim().toLowerCase().replace(/\s+/g, ' ')
}

/** «фамилия и.» / «фамилия и.о.» → части для нечёткого сравнения */
function parseShortMentionLabel(value: string): {
  last: string
  first: string
  middle?: string
} | null {
  const normalized = normalizeMentionLabel(value)
  const match = normalized.match(
    /^([a-zа-яё-]+)\s+([a-zа-яё])\.?(?:([a-zа-яё])\.?)?$/iu,
  )
  if (!match) {
    return null
  }

  return {
    last: match[1],
    first: match[2].toLowerCase(),
    middle: match[3]?.toLowerCase(),
  }
}

/** Совпадение подписей: точное или фамилия + инициал имени (отчество опционально). */
function mentionLabelsCompatible(a: string, b: string): boolean {
  const left = normalizeMentionLabel(a)
  const right = normalizeMentionLabel(b)
  if (!left || !right) {
    return false
  }
  if (left === right) {
    return true
  }

  const parsedA = parseShortMentionLabel(left)
  const parsedB = parseShortMentionLabel(right)
  if (parsedA && parsedB) {
    if (parsedA.last !== parsedB.last || parsedA.first !== parsedB.first) {
      return false
    }
    // «Т.» совместим с «Т.М.» и наоборот, если отчество у одной стороны нет
    if (!parsedA.middle || !parsedB.middle || parsedA.middle === parsedB.middle) {
      return true
    }
  }

  // Полное ФИО содержит short-форму или наоборот
  if (left.startsWith(right) || right.startsWith(left)) {
    return true
  }

  return false
}

/** Варианты подписи @-упоминания по display name (как при вставке из пикера). */
export function mentionLabelsFromDisplayName(displayName: string): string[] {
  const trimmed = displayName.trim()
  if (!trimmed || trimmed.startsWith('@')) {
    return []
  }

  const parts = trimmed.split(/\s+/).filter(Boolean)
  const labels = new Set<string>()

  labels.add(trimmed)

  const addShort = (last: string, first?: string, middle?: string) => {
    if (!last) {
      return
    }
    if (first) {
      const firstInitial = first[0]
      labels.add(`${last} ${firstInitial}.`)
      if (middle) {
        labels.add(`${last} ${firstInitial}.${middle[0]}.`)
      }
    } else {
      labels.add(last)
    }
  }

  // «Фамилия Имя Отчество»
  if (parts.length >= 2) {
    addShort(parts[0], parts[1], parts[2])
  }

  // «Имя Фамилия» / «Имя Отчество Фамилия»
  if (parts.length === 2) {
    addShort(parts[1], parts[0])
  } else if (parts.length >= 3) {
    addShort(parts[parts.length - 1], parts[0], parts[1])
  }

  if (parts.length === 1) {
    labels.add(parts[0])
  }

  const asPerson = {
    last_name: parts[0] || '',
    first_name: parts[1] || '',
    middle_name: parts.slice(2).join(' '),
  }
  const short = formatShortName(asPerson)
  if (short) {
    labels.add(short)
  }

  return [...labels]
}

/** Кандидат для @-пикера из участника чата */
export function toMentionCandidate(member: ChatMemberResponse): ChatMentionCandidate | null {
  const user = member.user
  if (!user) return null

  const mxid = member.user_uuid?.startsWith('@') ? member.user_uuid : ''
  const shortName
    = formatShortName(user)
    || getFullName(user)
    || (mxid ? fallbackNameFromMxid(mxid) : '')
    || String(user.id || '').trim()

  if (!shortName) return null

  const displayName = getFullName(user) || shortName

  return {
    key: mxid || String(user.id),
    mxid,
    displayName,
    mentionText: shortName,
    avatar: member.avatar || user.avatar || null,
    searchText: `${displayName} ${shortName} ${user.id} ${mxid}`.toLowerCase(),
  }
}

/** Активный фрагмент `@query` / `+query` слева от курсора */
export function extractMentionQuery(
  text: string,
  cursor: number,
): { start: number; query: string } | null {
  const safeCursor = Math.max(0, Math.min(cursor, text.length))
  const before = text.slice(0, safeCursor)
  const match = before.match(/(^|\s)[@+]([^\s@+]*)$/)
  if (!match) return null

  return {
    start: Math.max(before.lastIndexOf('@'), before.lastIndexOf('+')),
    query: match[2] || '',
  }
}

export function filterMentionCandidates(
  candidates: ChatMentionCandidate[],
  query: string,
  excludeKey?: string | null,
): ChatMentionCandidate[] {
  const q = query.trim().toLowerCase()
  const list = excludeKey
    ? candidates.filter(c => c.key !== excludeKey && c.mxid !== excludeKey)
    : candidates

  if (!q) return list

  return list.filter(c => c.searchText.includes(q))
}

/** MXID упоминаний, которые ещё есть в тексте */
export function collectMentionUserIds(
  text: string,
  candidates: ChatMentionCandidate[],
): string[] {
  const ids = new Set<string>()
  const sorted = [...candidates]
    .filter(c => c.mxid)
    .sort((a, b) => b.mentionText.length - a.mentionText.length)

  for (const candidate of sorted) {
    if (text.includes(`@${candidate.mentionText}`)) {
      ids.add(candidate.mxid)
    }
  }

  return [...ids]
}

/**
 * Сопоставить текст `@Фамилия И.` с MXID по списку упомянутых и подписям.
 * `usedMxids` — уже занятые в этом сообщении MXID (чтобы не вешать одного человека на два @).
 */
function resolveMxidForMentionText(
  mentionValue: string,
  mentionUserIds: string[] | undefined,
  labelByMxid?: Map<string, string[]>,
  usedMxids?: Set<string>,
): string | undefined {
  const needle = normalizeMentionLabel(mentionValue)
  if (!needle) {
    return undefined
  }

  const candidates = (mentionUserIds?.filter(id => id.startsWith('@')) ?? [])
    .filter(id => !usedMxids?.has(id))

  const pool = candidates.length
    ? candidates
    : [...(labelByMxid?.keys() ?? [])].filter(id => !usedMxids?.has(id))

  if (labelByMxid?.size) {
    for (const mxid of pool) {
      const labels = labelByMxid.get(mxid) ?? []
      if (labels.some(label => mentionLabelsCompatible(label, needle))) {
        return mxid
      }
    }
  }

  if (candidates.length === 1) {
    return candidates[0]
  }

  return undefined
}

/** `@Фамилия И.` / `@Фамилия И.О.` / `@Имя` для подсветки в пузыре */
const MENTION_DISPLAY_RE
  = /@([A-Za-zА-Яа-яЁё-]+(?:\s[A-Za-zА-Яа-яЁё]\.(?:[A-Za-zА-Яа-яЁё]\.)?)?)/gu

export function splitTextWithMentions(
  text: string,
  options?: {
    mentionUserIds?: string[]
    labelByMxid?: Map<string, string[]>
  },
): MentionTextSegment[] {
  if (!text) return []

  const parts: MentionTextSegment[] = []
  let lastIndex = 0
  const usedMxids = new Set<string>()
  const mentionUserIds = options?.mentionUserIds?.filter(id => id.startsWith('@')) ?? []

  for (const match of text.matchAll(MENTION_DISPLAY_RE)) {
    const start = match.index ?? 0
    if (start > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, start) })
    }
    const value = match[0]
    let mxid = resolveMxidForMentionText(
      value,
      mentionUserIds,
      options?.labelByMxid,
      usedMxids,
    )

    // Один оставшийся user_id → одно оставшееся @
    if (!mxid) {
      const remaining = mentionUserIds.filter(id => !usedMxids.has(id))
      if (remaining.length === 1) {
        mxid = remaining[0]
      }
    }

    if (mxid) {
      usedMxids.add(mxid)
    }

    parts.push({
      type: 'mention',
      value,
      mxid,
    })
    lastIndex = start + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) })
  }

  // Несколько несматченных @ и столько же оставшихся user_ids — по порядку в тексте
  const unresolved = parts.filter(
    (part): part is MentionTextSegment & { type: 'mention' } =>
      part.type === 'mention' && !part.mxid,
  )
  const remainingIds = mentionUserIds.filter(id => !usedMxids.has(id))
  if (unresolved.length > 0 && unresolved.length === remainingIds.length) {
    unresolved.forEach((part, index) => {
      part.mxid = remainingIds[index]
      usedMxids.add(remainingIds[index])
    })
  }

  return parts.length ? parts : [{ type: 'text', value: text }]
}
